import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinesses } from '@/context/BusinessContext';

const MAX_BUSINESSES = 4;

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.79 6.8-6.79a1 1 0 0 1 1.4 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path d="M10 4a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 10 4Z" />
    </svg>
  );
}

/**
 * Header control showing the active business and letting the user switch
 * between businesses or start adding another (up to the per-user cap).
 * Reads everything from BusinessProvider; switching only updates the active
 * id (which future, business-scoped passes will consume). A full-screen
 * backdrop handles click-away close (no document listeners needed).
 */
export function BusinessSwitcher() {
  const navigate = useNavigate();
  const { businesses, activeBusiness, activeBusinessId, setActiveBusinessId } = useBusinesses();
  const [open, setOpen] = useState(false);

  if (businesses.length === 0) return null;

  const label = activeBusiness?.name ?? businesses[0]?.name ?? 'Business';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 max-w-[12rem] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <span className="truncate">{label}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-2 w-60 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="max-h-64 overflow-y-auto py-1">
              {businesses.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActiveBusinessId(b.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <span className="truncate">{b.name}</span>
                  {b.id === activeBusinessId && <CheckIcon />}
                </button>
              ))}
            </div>

            {businesses.length < MAX_BUSINESSES && (
              <div className="border-t border-slate-200 py-1 dark:border-slate-700">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    navigate('/businesses/new');
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-600 transition hover:bg-slate-100 dark:text-brand-400 dark:hover:bg-slate-800"
                >
                  <PlusIcon />
                  Add business
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
